import {Icon} from 'leaflet';

import {Node} from '../interfaces/Node';

export const getNodeKey = (item: Node) => {
  const prefix =
    item.node_type === 'wifi_point'
      ? 'wifi'
      : item.node_type === 'bluetooth_node'
        ? 'bt'
        : 'cn';
  const id =
    item.node_type === 'wifi_point' || item.node_type === 'bluetooth_node'
      ? item.wifi_id
      : item.cn_id;
  return `${prefix}-${id}`;
};

export const getIsWifiIpVisible = (type: string | null) => {
  return ['A', 'D', 'G', 'O', null].includes(type);
};

export const getIsWifiMacSwitchVisible = (type: string | null) => {
  return ['A', 'B', 'G', 'O', null].includes(type);
};

export const getIsWifiMacRadio1Visible = (type: string | null) => {
  return ['A', 'D', null].includes(type);
};

export const getIsWifiMacRadio2Visible = (type: string | null) => {
  return ['A', null].includes(type);
};

export const getBatteryPercentage = (batteryVoltage: number) => {
  const value = batteryVoltage / 10;
  if (value >= 6) {
    return 100;
  } else if (value >= 5.8) {
    return 90;
  } else if (value >= 5.7) {
    return 80;
  } else if (value >= 5.6) {
    return 70;
  } else if (value >= 5.4) {
    return 60;
  } else if (value >= 5.3) {
    return 50;
  } else if (value >= 5.1) {
    return 40;
  } else if (value >= 5) {
    return 30;
  } else if (value >= 4.7) {
    return 20;
  } else if (value >= 4.5) {
    return 10;
  }
  return 0;
};

export const findTimingOrRoutingGroup = (
  nodes: Node[],
  type: 'routing' | 'timing',
  alertLevel: number,
  alarmLevel: number
): Node[][] => {
  const idMap = new Map<number, Node>();
  nodes.forEach((n) => {
    if (n.cn_external_id != null) {
      idMap.set(n.cn_external_id, n);
    }
  });

  const chainCache = new Map<number, Node[]>();

  const getParentChain = (
    objId: number | null,
    visited = new Set<number>()
  ): Node[] => {
    if (objId == null || visited.has(objId)) {
      return [];
    }

    if (chainCache.has(objId)) {
      return chainCache.get(objId)!;
    }

    const current = idMap.get(objId);
    if (!current) {
      return [];
    }

    visited.add(objId);

    const parentId =
      type === 'routing'
        ? current.cn_routing_parent_address
        : current.cn_timing_parent_address;

    const parentChain = getParentChain(parentId, visited);
    const fullChain = [...parentChain, current];

    chainCache.set(objId, fullChain);
    return fullChain;
  };

  const seenHashes = new Set<string>();
  const uniqueChains: Node[][] = [];

  for (const node of nodes) {
    const chain = getParentChain(node.cn_external_id);
    if (chain.length <= 1) {
      continue;
    }

    const hash = chain.map((n) => n.cn_external_id).join('>');
    if (!seenHashes.has(hash)) {
      seenHashes.add(hash);

      for (let i = 1; i < chain.length; i++) {
        const child = chain[i];

        (child as any).timing_connection_status =
          child.cn_timing_parent_rssi &&
          child.cn_timing_parent_rssi < alarmLevel
            ? 'alarm'
            : child.cn_timing_parent_rssi &&
                child.cn_timing_parent_rssi < alertLevel
              ? 'alert'
              : 'good';

        (child as any).routing_connection_status =
          child.cn_routing_parent_rssi &&
          child.cn_routing_parent_rssi < alarmLevel
            ? 'alarm'
            : child.cn_routing_parent_rssi &&
                child.cn_routing_parent_rssi < alertLevel
              ? 'alert'
              : 'good';
      }

      uniqueChains.push(chain);
    }
  }

  return uniqueChains;
};

export const createArrowIcon = (color: string): Icon =>
  new Icon({
    iconUrl: `data:image/svg+xml;utf8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16">
        <polygon points="5,0 10,16 0,16" fill="${color}" />
      </svg>
    `)}`,
    iconSize: [10, 16],
    iconAnchor: [5, 8],
  });

export enum CN_EVENT_TYPES {
  CN_ASSIGNED_MASTER = 101,
  CN_NO_HEARTBEAT = 102,
  CN_LOW_BATTERY = 103,
  CN_FEW_HEARTBEATS = 104,
  CN_NEW_CN_DETECTED = 109,
  CN_PIC_VERSION_HAS_CHANGED = 110,
  CN_CC1110_VERSION_HAS_CHANGED = 111,
  CN_UNREGISTERED_NETWORK = 112,
  CN_TOO_MANY_HEARTBEATS = 121,
  CN_CONFIGURATION_FAILED = 122,
  CN_SHUTDOWN_CONFIGURATION_FAILED = 130,
  CN_STOP_SHUTDOWN_CONFIGURATION_FAILED = 131,
  CN_CONFIGURATION_ACK = 132,
}

export const CN_EVENT_SOUNDS: {
  [value in CN_EVENT_TYPES]: string | null;
} = {
  [CN_EVENT_TYPES.CN_ASSIGNED_MASTER]: 'commtrac.sound_comm_assigned_master',
  [CN_EVENT_TYPES.CN_NO_HEARTBEAT]: 'commtrac.sound_comm_hbno_msgs',
  [CN_EVENT_TYPES.CN_LOW_BATTERY]: 'commtrac.sound_comm_low_battery',
  [CN_EVENT_TYPES.CN_FEW_HEARTBEATS]: 'commtrac.sound_comm_few_heartbeat',
  [CN_EVENT_TYPES.CN_NEW_CN_DETECTED]: 'commtrac.sound_comm_new_detected',
  [CN_EVENT_TYPES.CN_PIC_VERSION_HAS_CHANGED]:
    'commtrac.sound_comm_version_changed',
  [CN_EVENT_TYPES.CN_CC1110_VERSION_HAS_CHANGED]:
    'commtrac.sound_comm_version_changed',
  [CN_EVENT_TYPES.CN_UNREGISTERED_NETWORK]:
    'commtrac.sound_comm_unregistered_network',
  [CN_EVENT_TYPES.CN_TOO_MANY_HEARTBEATS]: 'commtrac.sound_comm_many_heartbeat',
  [CN_EVENT_TYPES.CN_CONFIGURATION_FAILED]: 'commtrac.sound_comm_config_failed',
  [CN_EVENT_TYPES.CN_SHUTDOWN_CONFIGURATION_FAILED]: null,
  [CN_EVENT_TYPES.CN_STOP_SHUTDOWN_CONFIGURATION_FAILED]: null,
  [CN_EVENT_TYPES.CN_CONFIGURATION_ACK]: null,
};

export const CN_EVENT_COLORS: {
  [value in CN_EVENT_TYPES]: string | null;
} = {
  [CN_EVENT_TYPES.CN_ASSIGNED_MASTER]:
    'commtrac-color.color_communication_node_assigned_master',
  [CN_EVENT_TYPES.CN_NO_HEARTBEAT]:
    'commtrac-color.color_communication_node_no_hb_no_msgs',
  [CN_EVENT_TYPES.CN_LOW_BATTERY]:
    'commtrac-color.color_communication_node_low_battery',
  [CN_EVENT_TYPES.CN_FEW_HEARTBEATS]:
    'commtrac-color.color_communication_node_few_heartbeat',
  [CN_EVENT_TYPES.CN_NEW_CN_DETECTED]:
    'commtrac-color.color_communication_node_new_detected',
  [CN_EVENT_TYPES.CN_PIC_VERSION_HAS_CHANGED]:
    'commtrac-color.color_communication_node_version_changed',
  [CN_EVENT_TYPES.CN_CC1110_VERSION_HAS_CHANGED]:
    'commtrac-color.color_communication_node_version_changed',
  [CN_EVENT_TYPES.CN_UNREGISTERED_NETWORK]:
    'commtrac-color.color_communication_node_unregistered_network',
  [CN_EVENT_TYPES.CN_TOO_MANY_HEARTBEATS]:
    'commtrac-color.color_communication_node_many_heartbeat',
  [CN_EVENT_TYPES.CN_CONFIGURATION_FAILED]: null,
  [CN_EVENT_TYPES.CN_SHUTDOWN_CONFIGURATION_FAILED]: null,
  [CN_EVENT_TYPES.CN_STOP_SHUTDOWN_CONFIGURATION_FAILED]: null,
  [CN_EVENT_TYPES.CN_CONFIGURATION_ACK]: null,
};
